<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('foreigners.new_foreigner') }}
    </h1>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-user-avatar>
          <icon-user-add-v2 class="tw-w-6 tw-h-6" />
        </i-user-avatar>
        <i-form-item
          :label="$t('common.fields.first_name')"
          prop="first_name"
        >
          <el-input
            v-model="form.first_name"
          />
        </i-form-item>
        <i-form-item
          :label="$t('common.fields.last_name')"
          prop="last_name"
        >
          <el-input
            v-model="form.last_name"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.gender')"
          prop="gender"
        >
          <el-select
            v-model="form.gender"
            placeholder=""
            filterable
          >
            <el-option
              v-for="item in genders"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>

        <i-form-item
          :label="$t('common.fields.citizenship')"
          prop="profile.citizenship_id"
        >
          <el-select
            v-model="form.profile.citizenship_id"
            placeholder=""
            filterable
          >
            <el-option
              v-for="item in countries"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.email')"
          prop="email"
        >
          <el-input
            v-model="form.email"
            type="email"
          />
        </i-form-item>

        <i-form-item
          v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
          :label="$t('common.fields.phone')"
          prop="phone_primary"
        >
          <el-input
            v-model="form.phone_primary"
            type="tel"
          />
        </i-form-item>

        <i-form-item
          v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
          :label="$t('common.fields.phone')"
          prop="phone_secondary"
        >
          <el-input
            v-model="form.phone_secondary"
            type="tel"
          />
        </i-form-item>
      </div>

      <div
        v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
        class="tw-flex tw-items-start tw-gap-2"
      >
        <i-form-item
          :label="$t('common.fields.company')"
          prop="profile.client_company_id"
        >
          <el-select
            v-model="form.profile.client_company_id"
            placeholder=""
            filterable
            :filter-method="clientCompaniesFilterMethod"
            @visible-change="handleClientCompaniesVisibleChange"
          >
            <el-option
              v-for="item in filteredClientCompanies"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
      </div>

      <div
        v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
        class="tw-flex tw-items-start tw-gap-2"
      >
        <i-form-item
          :label="$t('main.me.interface_language')"
          prop="interface_language"
        >
          <el-select
            v-model="form.interface_language"
            placeholder=""
            filterable
          >
            <el-option
              v-for="item in languages"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>

        <i-form-item
          :label="$t('foreigners.filters.assigned_to')"
          prop="profile.assigned_to_id"
        >
          <el-select
            v-model="form.profile.assigned_to_id"
            placeholder=""
            filterable
            :filter-method="internalUsersFilterMethod"
            @visible-change="handleInternalUsersVisibleChange"
          >
            <el-option
              v-for="item in filteredInternalUsers"
              :key="item.id"
              :label="item.fullname"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
      </div>
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { internalUsersFilterMixin } from '@/mixins/internalUsersFilterMixin';
import { clientCompanyFilterMixin } from '@/mixins/clientCompanyFilterMixin';

const FORM_BASE = {
  first_name: '',
  last_name: '',
  email: '',
  profile: {
    client_company_id: '',
    assigned_to_id: '',
    citizenship_id: '',
  },
  gender: '',
  phone_primary: '',
  phone_secondary: '',
  interface_language: 1,
};

export default {
  mixins: [internalUsersFilterMixin, clientCompanyFilterMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      form: _cloneDeep(FORM_BASE),
      formRules: {
        first_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        last_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        gender: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        'profile.citizenship_id': [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        email: [
          { required: true, message: 'Required', trigger: 'blur' },
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' },
        ],
        'profile.client_company_id': [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        interface_language: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        'profile.assigned_to_id': [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      countries: (state) => state.v2.shared.enums.enums.countries,
      genders: (state) => state.v2.shared.enums.enums.genders,
      familyRelations: (state) => state.v2.shared.enums.enums.family_relations,
      languages: (state) => state.v2.shared.enums.enums.languages,
    }),
    ...mapGetters({
      internalUsers: 'v2/admin/cms/internal/users/selectOptions',
      clientCompanies: 'v2/admin/cms/clients/companies/selectOptions',
    }),
  },

  mounted() {
    this.$store.dispatch('v2/admin/cms/clients/companies/loadAllCompanies');
    this.$store.dispatch('v2/admin/cms/internal/users/loadAllUsers');
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.form = _cloneDeep(FORM_BASE);
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/foreigners/foreigners/addForeigner', {
          payload: this.form,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch(error) {
        const message = _get(error, 'response.data.data.relation_with_id');
        this.$message.error({
          message: this.$capitalize(message || 'Error'),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
